import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, KeyRound, Plus, X, Pin } from "lucide-react";
import { SECTION_COLORS, findPage } from "../../navigation/navConfig";
import { useEpingles } from "../../navigation/useEpingles";
import EpinglesPicker from "./EpinglesPicker";
import FilDuJour from "./FilDuJour";
import PlanningJourFutur from "./PlanningJourFutur";
import FuturCards from "./FuturCards";
import FuturMenu from "./FuturMenu";

const ECRAN_LABEL = { COURRIER: 'Courrier', CHIRURGIE: 'Chirurgie', SUIVI: 'Suivi patient' };

const NavHomeFutur = ({ tree, user, badges, onGo, onGoScreen }) => {
  const [picker, setPicker] = useState(false);
  const orbitRef = useRef(null);
  const paused = useRef(false);
  // Anneau 3D : chaque écran suit une ellipse (comme un anneau planétaire) ; devant = plus grand, derrière = plus petit et estompé
  useEffect(() => {
    let rot = 0, last = performance.now(), raf;
    const tick = (now) => {
      if (!paused.current) rot = (rot + (now - last) * 360 / 60000) % 360;
      last = now;
      const el = orbitRef.current;
      if (el) {
        const slots = el.querySelectorAll('.opn-orbit__slot');
        const n = slots.length, rx = el.clientWidth / 2 - 90, ry = Math.max(80, el.clientHeight * 0.24);
        slots.forEach((sl, i) => {
          const th = ((360 / n) * i + rot) * Math.PI / 180;
          const x = Math.cos(th) * rx, y = Math.sin(th) * ry, d = (Math.sin(th) + 1) / 2;
          sl.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) scale(${0.68 + 0.32 * d})`;
          sl.style.opacity = String(0.45 + 0.55 * d);
          sl.style.zIndex = d > 0.5 ? 4 : 1;
          sl.style.filter = d > 0.5 ? 'none' : 'blur(0.4px)';
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  const { pins, keys, add, remove } = useEpingles(tree);
  const sections = tree.sections;
  const glow = SECTION_COLORS[sections[0]?.id] || '#22d3ee';
  const heure = new Date().getHours();
  const salut = heure < 18 ? 'Bonjour' : 'Bonsoir';
  const orbit = [...pins.map(p => ({ type: 'pin', ...p })), { type: 'add', key: '__add' }];

  return (
    <div className="opn-fx" data-testid="nav-home-futur">
      <div className="opn-fx__hero">
        <div>
          <h1 className="opn-fx__title" data-testid="home-greeting">{salut} {user?.prenom}.</h1>
          <p className="opn-fx__tagline">L'intelligence médicale, en mouvement.</p>
          <PlanningJourFutur user={user} />
        </div>

        <div className="opn-fx__center">
          <div className="opn-fx__stage" aria-label="Mes épingles autour de l'œil" data-testid="fx-stage">
            <div className="opn-fx__glow" style={{ '--sec': glow }} />
            <div className="opn-orbit__ring opn-orbit__ring--back" />
            <img src="/img/eye-3d.png" alt="" className="opn-fx__eye" data-testid="fx-eye" />
            <div className="opn-orbit__ring opn-orbit__ring--front" />
            <div className="opn-orbit" ref={orbitRef} onMouseEnter={() => { paused.current = true; }} onMouseLeave={() => { paused.current = false; }} onFocus={() => { paused.current = true; }} onBlur={() => { paused.current = false; }} data-testid="fx-ring">
              {orbit.map((o) => {
                if (o.type === 'add') return (
                  <div key={o.key} className="opn-orbit__slot">
                    <button type="button" className="opn-screen opn-screen--add" onClick={() => setPicker(true)} data-testid="fx-panel-add" title="Choisir mes épingles">
                      <span className="opn-screen__bar"><i /><i /><i /></span>
                      <span className="opn-screen__body"><Plus className="h-5 w-5" /><span>Ajouter une épingle</span></span>
                    </button>
                  </div>
                );
                const PI = o.page.icon; const n = badges?.[o.page.badge]; const col = SECTION_COLORS[o.section?.id];
                return (
                  <div key={o.key} className="opn-orbit__slot">
                    <button type="button" className="opn-screen" style={{ '--sec': col }} onClick={() => onGo(o.target, o.ecran)} data-testid={`fx-pin-${o.page.id}${o.ecran ? '-' + o.ecran.toLowerCase() : ''}`}>
                      <span className="opn-screen__bar"><i /><i /><i /><small>{o.section?.label}</small></span>
                      <span className="opn-screen__body">
                        <PI className="h-5 w-5 opn-screen__icon" />
                        <span className="opn-screen__label">{o.page.label}{o.ecran ? ` · ${ECRAN_LABEL[o.ecran] || o.ecran}` : ''}</span>
                        {n > 0 && <span className="opn-menu__badge">{n}</span>}
                      </span>
                      <svg className="opn-screen__wave" viewBox="0 0 120 20" preserveAspectRatio="none" aria-hidden="true"><path d="M0 14 L12 12 L22 16 L34 8 L46 13 L58 6 L70 12 L82 9 L94 15 L106 7 L120 11" /></svg>
                    </button>
                    <button type="button" className="opn-fx__unpin" onClick={(e) => { e.stopPropagation(); remove(o.key); }} aria-label={`Retirer ${o.page.label}`} data-testid={`fx-unpin-${o.page.id}`}><X className="h-3 w-3" /></button>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="opn-fx__quick">
            {findPage(tree, 'outils/messages') && <button type="button" className="opn-fx__round" title="Messages" onClick={() => onGo('outils/messages')} data-testid="fx-quick-messages"><MessageCircle className="h-5 w-5" />{badges?.messages > 0 && <span className="opn-menu__badge">{badges.messages}</span>}</button>}
            {findPage(tree, 'outils/coffre-fort') && <button type="button" className="opn-fx__round" title="Mon Coffre-Fort" onClick={() => onGo('outils/coffre-fort')} data-testid="fx-quick-coffre"><KeyRound className="h-5 w-5" /></button>}
            <button type="button" className="opn-fx__round" title="Choisir mes épingles" onClick={() => setPicker(true)} data-testid="fx-quick-epingles"><Pin className="h-5 w-5" /></button>
          </div>
        </div>

        <FilDuJour tree={tree} user={user} onGoScreen={onGoScreen} />
      </div>

      <FuturCards tree={tree} user={user} badges={badges} onGoScreen={onGoScreen} />

      <FuturMenu tree={tree} badges={badges} onGo={onGo} />

      {picker && <EpinglesPicker tree={tree} keys={keys} onAdd={add} onRemove={remove} onClose={() => setPicker(false)} />}
    </div>
  );
};

export default NavHomeFutur;
